import { findLast } from '@/utils';

import type { RecommendationStoreAdapter } from './storeAdapter';
import type { RecommendationItem, RecommendationStoreState } from './types';
import { defaultState } from './utils';

const MAX_ITEMS = 30;

export class RecommendationStore {
    private adapter: RecommendationStoreAdapter;
    private currentRecommendationId: string | null = null;

    state: RecommendationStoreState = defaultState;

    constructor(storeAdapter: RecommendationStoreAdapter) {
        this.adapter = storeAdapter;
        this.state = this.adapter.getAll();
        this.limitStoreSize();
    }

    addItem(item: RecommendationItem) {
        this.state.items.push(item);
        this.limitStoreSize();
        this.save();
    }

    setRecommendationId(id: string): void {
        this.currentRecommendationId = id;
    }

    getRecommendationId(): string | null {
        return this.currentRecommendationId;
    }

    /**
     * Finds the ID of the source recommendation based on item ID.
     *
     * @param itemId - The ID of the current item.
     * @returns The ID of the source recommendation.
     */
    findSourceRecommendationId(itemId: string): string | null {
        const item = findLast(this.state.items, (item) => item.id === itemId);

        if (!item) {
            return null;
        }

        return item.recommendedBy;
    }

    // avoid keeping too many items in the store
    private limitStoreSize(): void {
        if (this.state.items.length > MAX_ITEMS) {
            const itemsToKeep = this.state.items.slice(-MAX_ITEMS);
            this.state = { ...this.state, items: itemsToKeep };
            this.save();
        }
    }

    private save() {
        this.adapter.save(this.state);
    }
}
